import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, set, onValue, push, off, update, remove, query, limitToLast, get, runTransaction } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { database } from '../firebase/config';
import {
  buildActivityFeedEntry,
  buildSharedVaultEntry,
  buildActiveEffectEntry,
  buildPushedContentEntry,
} from '../services/partyWriteHelpers';
import { effectCalculator } from '../services/effectCalculator';
import { reportError } from '../services/errorReporter';

/**
 * [PURPOSE]: Enhanced party sync hook with DM tools, shared vault, and spell targeting
 * [WHY]: Enable full D&D group collaboration - DMs push content, players cast spells on allies, shared loot
 * [INTEGRATION]: Replaces usePartySync with comprehensive party features
 *
 * @param {string} partyId - Unique party identifier
 * @param {string} characterId - Current character's unique ID
 * @param {object} characterData - Current character's data
 * @param {array} localActivityLog - Local activity log entries
 * @returns {object} Enhanced party sync state and methods
 */
export const useEnhancedPartySync = (partyId, characterId, characterData = {}, localActivityLog = []) => {
  // Core state
  const [partyMembers, setPartyMembers] = useState({});
  const [partyActivityLog, setPartyActivityLog] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // [NEW] Enhanced features state
  const [sharedVaultItems, setSharedVaultItems] = useState({});
  const [pushedContent, setPushedContent] = useState({
    shops: {},
    notes: {},
    calendarEvents: {},
    lootRolls: {}
  });
  const [activeEffects, setActiveEffects] = useState([]); // Effects on current character
  const [allPartyEffects, setAllPartyEffects] = useState({}); // All effects across party

  // Permission state
  const [isDM, setIsDM] = useState(false);
  const [partyMetadata, setPartyMetadata] = useState(null);

  const lastSyncedActivityRef = useRef(null);

  // [PSR-018 FIX — MAY 2026]: Reset lastSyncedActivityRef when partyId
  //   changes. Pre-fix the ref retained its value across party switches —
  //   leaving party A and joining party B with the old ref still pointing
  //   at A's last-synced ID could cause ALL local activity log entries to
  //   replay into party B (because the for-of loop never finds the
  //   old-A id and walks the entire log). Now we wipe the ref on every
  //   partyId transition.
  useEffect(() => {
    lastSyncedActivityRef.current = null;
  }, [partyId]);

  // ============================================
  // CORE SYNC FUNCTIONS (Existing)
  // ============================================

  const syncActivity = useCallback((activityEntry) => {
    if (!partyId || !characterId || !database) {
      return;
    }

    // [BATCH 1 — 2026-05-03]: Tag every activity entry with actorUid (= auth.uid)
    // so the database.rules.json `activityFeed/$entryId` rule can reject forged
    // entries from other users. Skip the write if no auth (rules would reject anyway).
    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) {
      return;
    }

    try {
      const activityRef = ref(database, `parties/${partyId}/activityFeed`);
      const newActivityRef = push(activityRef);

      set(newActivityRef, buildActivityFeedEntry({
        authUid,
        characterId,
        characterName: characterData.name,
        activityEntry,
      }));

    } catch (error) {
      reportError(error, { area: 'party_sync_activity', extra: { partyId, characterId } });
      setSyncError(error.message);
    }
  }, [partyId, characterId, characterData.name]);

  const updateCharacterStatus = useCallback((updates) => {
    if (!partyId || !characterId || !database) return;

    try {
      const memberRef = ref(database, `parties/${partyId}/members/${characterId}`);
      update(memberRef, {
        ...updates,
        lastSeen: new Date().toISOString()
      });
    } catch (error) {
      reportError(error, { area: 'party_update_character_status', extra: { partyId, characterId } });
    }
  }, [partyId, characterId]);

  // ============================================
  // [NEW] SHARED VAULT FUNCTIONS
  // ============================================

  /**
   * Add an item to the shared party vault
   */
  const addToSharedVault = useCallback(async (item) => {
    if (!partyId || !characterId || !database) {
      console.warn('Cannot add to vault: missing party/character ID or database');
      return false;
    }

    try {
      // [BATCH 1 — 2026-05-03]: Tag vault adds with addedByUid (= auth.uid)
      // for parity with member ownership tracking.
      const authUid = getAuth()?.currentUser?.uid;
      if (!authUid) {
        return false;
      }

      const vaultRef = ref(database, `parties/${partyId}/sharedVault`);
      const newItemRef = push(vaultRef);

      await set(newItemRef, buildSharedVaultEntry({
        authUid,
        characterId,
        characterName: characterData.name,
        item,
        itemId: newItemRef.key,
      }));

      // Log to activity feed
      syncActivity({
        type: 'vault',
        message: `added ${item.name} to shared vault`,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      reportError(error, { area: 'party_vault_add', extra: { partyId } });
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, syncActivity]);

  /**
   * Take an item from the shared vault.
   *
   * [PSR-006 FIX MAY 2026]: Uses `runTransaction` for first-take-wins semantics.
   *   Pre-fix, two members could both call `remove()` on the same path
   *   (idempotent — gone-then-gone-again succeeds for both), both syncActivity'd
   *   "claimed X", and both materialized the item into local inventory.
   *   The vault lost ONE item but TWO copies were created.
   *
   *   Now: the transaction reads the current value, captures it, deletes it.
   *   If another member already took it, `current === null` triggers an abort
   *   (returning `undefined` from the updateFunction cancels). Loser gets a
   *   "claimed by other" return path (null + console warn).
   */
  const takeFromSharedVault = useCallback(async (vaultItemId) => {
    if (!partyId || !characterId || !database) {
      console.warn('Cannot take from vault: missing party/character ID');
      return null;
    }

    try {
      const vaultItemRef = ref(database, `parties/${partyId}/sharedVault/${vaultItemId}`);

      // Capture pre-transaction value inside the updateFunction so we can
      //   return the item to the caller after the transaction commits.
      let snapshotBeforeTake = null;
      const txResult = await runTransaction(vaultItemRef, (current) => {
        if (current === null || current === undefined) {
          // Item is already taken — abort (returning undefined cancels)
          return;
        }
        snapshotBeforeTake = current;
        return null; // setting to null deletes the path
      });

      if (!txResult.committed || !snapshotBeforeTake) {
        console.warn('Vault take aborted: item was claimed by another member');
        return null;
      }

      const takenItem = snapshotBeforeTake.item;

      // Log activity (only on successful first-take)
      syncActivity({
        type: 'vault',
        message: `claimed ${takenItem?.name || 'item'} from shared vault`,
        timestamp: new Date().toISOString()
      });

      return takenItem;
    } catch (error) {
      reportError(error, { area: 'party_vault_take', extra: { partyId } });
      setSyncError(error.message);
      return null;
    }
  }, [partyId, characterId, syncActivity]);

  /**
   * Remove an item from the shared vault (DM only)
   * [BATCH 13b / P1 A-18 — APR 2026]: now async + awaits the
   *   Firebase remove. Pre-fix the call was fire-and-forget — the
   *   "removed X from shared vault" activity log fired even when
   *   Firebase rejected the remove (e.g. permission denied,
   *   network error).
   */
  // [PSR-011 NOTE — MAY 2026]: Activity logging for vault remove happens
  //   HERE (hook layer) via syncActivity. PartyContext.removeItemFromSharedVault
  //   wrapper emits the systemBus EVENTS.SHARED_VAULT_ITEM_REMOVED event for
  //   UI subscribers, but does NOT call logActivity itself.
  //   Contract: HOOK = activity log; CONTEXT = systemBus event for UI.
  //   If a future subscriber wires up logActivity on SHARED_VAULT_ITEM_REMOVED,
  //   remove the syncActivity call here to prevent doubled feed entries
  //   (the C4 fix at applyEffectToAlly L569-573 is the precedent for this pattern).
  const removeFromSharedVault = useCallback(async (vaultItemId) => {
    if (!partyId || !characterId || !database || !isDM) {
      console.warn('Cannot remove from vault: not DM or missing data');
      return false;
    }

    try {
      const vaultItemRef = ref(database, `parties/${partyId}/sharedVault/${vaultItemId}`);
      await remove(vaultItemRef);

      const vaultItem = sharedVaultItems[vaultItemId];
      if (vaultItem) {
        syncActivity({
          type: 'vault',
          message: `removed ${vaultItem.item.name} from shared vault`,
          timestamp: new Date().toISOString()
        });
      }

      return true;
    } catch (error) {
      reportError(error, { area: 'party_vault_remove', extra: { partyId } });
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, isDM, sharedVaultItems, syncActivity]);

  // ============================================
  // [NEW] DM PUSH CONTENT FUNCTIONS
  // ============================================

  /**
   * Push a shop to all party members
   */
  const pushShopToParty = useCallback(async (shop) => {
    if (!partyId || !characterId || !database || !isDM) {
      console.warn('Cannot push shop: not DM or missing data');
      return false;
    }

    // [BATCH 1.5 — S-4]: Rules require auth.uid === metadata.dmUid for any
    // pushedContent write. Skip silently if no auth (rule would reject anyway).
    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) {
      return false;
    }

    try {
      const shopRef = ref(database, `parties/${partyId}/pushedContent/shops`);
      const newShopRef = push(shopRef);

      // [FA-002 FIX 2026-05-09]: await the set() so a Firebase rejection
      // (rules denial, network) lands in the catch block. Pre-fix, this
      // returned true synchronously even when the write failed silently.
      await set(newShopRef, {
        ...buildPushedContentEntry({
          authUid,
          dmCharacterId: characterId,
          dmCharacterName: characterData.name,
          contentKind: 'shop',
          content: shop,
        }),
        shop,
        viewedBy: {}
      });

      syncActivity({
        type: 'dm_push',
        message: `pushed shop: ${shop.name || 'Unnamed Shop'}`,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      reportError(error, { area: 'party_push_shop', extra: { partyId } });
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, isDM, syncActivity]);

  /**
   * Push a journal note to all party members
   */
  const pushNoteToParty = useCallback(async (note) => {
    if (!partyId || !characterId || !database || !isDM) {
      console.warn('Cannot push note: not DM or missing data');
      return false;
    }

    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) return false;

    try {
      const noteRef = ref(database, `parties/${partyId}/pushedContent/notes`);
      const newNoteRef = push(noteRef);

      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch.
      await set(newNoteRef, {
        ...buildPushedContentEntry({
          authUid,
          dmCharacterId: characterId,
          dmCharacterName: characterData.name,
          contentKind: 'note',
          content: note,
        }),
        note,
        viewedBy: {}
      });

      syncActivity({
        type: 'dm_push',
        message: `shared note: ${note.title || 'Untitled'}`,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('❌ Error pushing note:', error);
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, isDM, syncActivity]);

  /**
   * Push a calendar event to sync party calendars
   */
  const pushCalendarEventToParty = useCallback(async (event) => {
    if (!partyId || !characterId || !database || !isDM) {
      console.warn('Cannot push calendar event: not DM or missing data');
      return false;
    }

    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) return false;

    try {
      const eventRef = ref(database, `parties/${partyId}/pushedContent/calendarEvents`);
      const newEventRef = push(eventRef);

      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch.
      await set(newEventRef, {
        ...buildPushedContentEntry({
          authUid,
          dmCharacterId: characterId,
          dmCharacterName: characterData.name,
          contentKind: 'calendarEvent',
          content: event,
        }),
        event,
        acceptedBy: {}
      });

      syncActivity({
        type: 'dm_push',
        message: `synced calendar to game day ${event.gameDay || event}`,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('❌ Error pushing calendar event:', error);
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, isDM, syncActivity]);

  /**
   * Push loot roll results to party
   */
  const pushLootRollToParty = useCallback(async (rollData) => {
    if (!partyId || !characterId || !database || !isDM) {
      console.warn('Cannot push loot roll: not DM or missing data');
      return false;
    }

    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) return false;

    try {
      const rollRef = ref(database, `parties/${partyId}/pushedContent/lootRolls`);
      const newRollRef = push(rollRef);

      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch.
      await set(newRollRef, {
        ...buildPushedContentEntry({
          authUid,
          dmCharacterId: characterId,
          dmCharacterName: characterData.name,
          contentKind: 'lootRoll',
          content: rollData,
        }),
        roll: rollData,
        results: rollData.results || []
      });

      syncActivity({
        type: 'dm_push',
        message: `rolled loot: ${rollData.tableName || 'Unknown Table'}`,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('❌ Error pushing loot roll:', error);
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, isDM, syncActivity]);

  /**
   * Claim a loot roll result pushed by the DM — records claim in Firebase,
   * returns the roll entry so the caller can add it to inventory.
   * Multiple party members can independently claim the same roll (no need/greed
   * in v1.0 — DM adjudicates verbally).
   */
  const claimLootItem = useCallback(async (rollId) => {
    if (!partyId || !characterId || !database) return null;
    const rollEntry = pushedContent.lootRolls[rollId];
    if (!rollEntry) return null;

    try {
      const claimRef = ref(database, `parties/${partyId}/pushedContent/lootRolls/${rollId}/claims/${characterId}`);
      await set(claimRef, {
        name: characterData.name || 'Unknown',
        claimedAt: new Date().toISOString()
      });

      syncActivity({
        type: 'items',
        message: `claimed ${rollEntry.roll?.result?.result || rollEntry.roll?.result?.name || 'loot item'} from DM loot roll`,
        timestamp: new Date().toISOString()
      });

      return rollEntry;
    } catch (error) {
      console.error('❌ Error claiming loot item:', error);
      return null;
    }
  }, [partyId, characterId, characterData.name, pushedContent.lootRolls, syncActivity]);

  /**
   * [B1-B3 FIX]: Mark a DM-pushed note / calendar event / shop as viewed by
   * the current character. Writes to
   * `parties/{partyId}/pushedContent/{type}/{id}/viewedBy/{characterId}`.
   * Used by party members to dismiss the "unread" indicator.
   */
  const markPushedContentViewed = useCallback(async (contentType, contentId) => {
    if (!partyId || !characterId || !database || !contentType || !contentId) return;
    try {
      await set(
        ref(database, `parties/${partyId}/pushedContent/${contentType}/${contentId}/viewedBy/${characterId}`),
        true
      );
    } catch (err) {
      console.error('❌ Error marking pushed content viewed:', err);
    }
  }, [partyId, characterId]);

  // ============================================
  // [FIX JAN 2026 - Issue #20] DM CLAIMING FUNCTIONS
  // ============================================

  /**
   * Claim or release DM status for the current character
   * [PURPOSE]: Allow a player to toggle their DM status for party management
   * [WHY]: DMs need permissions to push content, manage effects, and control party features
   * [INTEGRATION]: Updates Firebase metadata.dmId which triggers listener on line 431
   */
  const claimDM = useCallback(async (claim) => {
    if (!partyId || !characterId || !database) {
      console.warn('Cannot claim DM: missing party/character ID or database');
      return false;
    }

    try {
      const metadataRef = ref(database, `parties/${partyId}/metadata`);

      if (claim) {
        // Claim DM status
        await update(metadataRef, {
          dmId: characterId,
          dmName: characterData.name || 'Unknown',
          dmClaimedAt: new Date().toISOString()
        });

        syncActivity({
          type: 'system',
          message: 'has claimed DM status 👑',
          timestamp: new Date().toISOString()
        });

      } else {
        // Release DM status
        await update(metadataRef, {
          dmId: null,
          dmName: null,
          dmClaimedAt: null
        });

        syncActivity({
          type: 'system',
          message: 'has released DM status 🎭',
          timestamp: new Date().toISOString()
        });

      }

      return true;
    } catch (error) {
      console.error('❌ Error claiming DM status:', error);
      // [PSR-016 FIX — MAY 2026]: Surface specific "seat already taken" message
      //   on rule denial. Pre-fix the panel showed a generic Firebase error
      //   when two members raced to claim DM on a stale listener — first wins
      //   the rule check, second got opaque PERMISSION_DENIED. Now we detect
      //   the rule-denial pattern and translate to user-friendly text.
      const msg = String(error?.message || '');
      const isPermissionDenied = /PERMISSION_DENIED|permission_denied|denied/i.test(msg);
      if (claim && isPermissionDenied) {
        setSyncError('Someone else just claimed DM. Refresh to see the current DM.');
      } else {
        setSyncError(error.message);
      }
      return false;
    }
  }, [partyId, characterId, characterData.name, syncActivity]);

  // [FIX APR 2026 Phase 3D.7]: DM action — clear entire activity log.
  // [WHY]: User asked "the recent activity log cant be cleared, and is just kinda rolling"
  //        and flagged bandwidth cost. Now the DM can purge it explicitly.
  const clearActivityLog = useCallback(async () => {
    if (!partyId || !isDM || !database) return false;
    try {
      await remove(ref(database, `parties/${partyId}/activityFeed`));
      return true;
    } catch (err) {
      console.error('❌ Error clearing activity log:', err);
      return false;
    }
  }, [partyId, isDM]);

  // [FIX APR 2026 Phase 3D.4]: DM action — kick a stale or unwanted member.
  // [WHY]: Leave/stale cleanup was missing entirely; ghosts accumulated in the
  //        members list after devices went offline or users switched characters.
  // [BSR-003 FIX — MAY 2026]: Pre-fix kickMember only removed /members/$characterId
  //   but left /memberUids/$uid intact. Because Firebase rules read root.child(
  //   'parties/$partyId/memberUids/$uid').exists() as the gate for read/write
  //   access, the kicked user retained full access (could re-add themselves,
  //   read shared vault, write to bastion, etc.). Now we look up ownerUid from
  //   the member record BEFORE removal and clean up both nodes (matches
  //   AuthContext.handleDeleteAccount's leave-party flow).
  const kickMember = useCallback(async (memberCharacterId) => {
    if (!partyId || !isDM || !database || !memberCharacterId) return false;
    try {
      // Read ownerUid before removing the member record so we know which uid
      // to clean from memberUids.
      let ownerUid = null;
      try {
        const memberSnap = await get(ref(database, `parties/${partyId}/members/${memberCharacterId}/ownerUid`));
        ownerUid = memberSnap.exists() ? memberSnap.val() : null;
      } catch (lookupErr) {
        console.warn('[kickMember] ownerUid lookup failed (member may already be gone):', lookupErr);
      }

      await remove(ref(database, `parties/${partyId}/members/${memberCharacterId}`));

      if (ownerUid) {
        try {
          await remove(ref(database, `parties/${partyId}/memberUids/${ownerUid}`));
        } catch (uidErr) {
          // Non-fatal — the character record is already gone; if memberUids
          // cleanup fails (e.g., rule rejection because another character on
          // the same uid is still in the party), log it but treat the kick as
          // successful from the DM's perspective.
          console.warn('[kickMember] memberUids cleanup failed:', uidErr);
        }
      }

      return true;
    } catch (err) {
      console.error('❌ Error kicking member:', err);
      return false;
    }
  }, [partyId, isDM]);

  // ============================================
  // [NEW] SPELL/EFFECT TARGETING FUNCTIONS
  // ============================================

  /**
   * Apply an effect to an ally (spell targeting)
   */
  const applyEffectToAlly = useCallback(async (targetId, effectData) => {
    if (!partyId || !characterId || !database) {
      console.warn('Cannot apply effect: missing party/character ID');
      return false;
    }

    // [BATCH 1.5 — S-5]: Rules require casterUid === auth.uid on activeEffects
    // entries. Skip silently if no auth (rule would reject anyway).
    const authUid = getAuth()?.currentUser?.uid;
    if (!authUid) return false;

    try {
      const effectRef = ref(database, `parties/${partyId}/activeEffects/${targetId}`);
      const newEffectRef = push(effectRef);

      const effect = buildActiveEffectEntry({
        authUid,
        casterCharacterId: characterId,
        casterCharacterName: characterData.name,
        targetCharacterId: targetId,
        effect: {
          effectName: effectData.name,
          effectType: effectData.type || 'quick_bonus',
          casterId: characterId,
          casterName: characterData.name || 'Unknown',
          duration: effectData.duration || 0,
          data: effectData.data || {},
          expiresAt: effectData.expiresAt || null,
        },
      });

      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch.
      await set(newEffectRef, effect);
      // [C4 FIX]: Removed duplicate syncActivity here. The caller (App.jsx) already
      //   calls logActivity("Cast X on Y", 'spells') which syncs to the party feed
      //   via the activity useEffect. Writing here AND there created two identical
      //   party feed entries for every ally-targeted cast.
      return true;
    } catch (error) {
      console.error('❌ Error applying effect:', error);
      setSyncError(error.message);
      return false;
    }
  }, [partyId, characterId, characterData.name, syncActivity]);

  /**
   * [C2 FIX]: Update an ally's HP in the party panel when a healing spell lands.
   * This updates the DISPLAY HP (parties/{partyId}/members/{targetId}/hp) so the
   * party panel shows the correct HP immediately. The ally's own client will
   * re-sync from their character sheet on next status update (30s heartbeat or
   * when they take/gain HP locally), at which point the party panel reflects
   * their true sheet HP.
   */
  const healAllyInParty = useCallback(async (targetId, healAmount) => {
    if (!partyId || !database || !targetId || !healAmount || healAmount <= 0) return false;
    const member = partyMembers[targetId];
    if (!member) return false;
    // [SPR-009 FIX — MAY 2026]: Pre-fix used `maxHp ?? currentHp` as the upper
    //   bound, which made the heal a no-op when maxHp wasn't yet synced
    //   (Math.min(currentHp + heal, currentHp) === currentHp). Now we fall
    //   back to currentHp + healAmount as the cap so the heal at least
    //   shows up in the party panel — heartbeat resync (line ~881-915)
    //   corrects any brief over-cap before the next render tick.
    const currentHp = member.hp ?? 0;
    const safeUpperBound = currentHp + healAmount; // pre-clamp value
    const maxHp = member.maxHp ?? safeUpperBound;
    const newHp = Math.min(safeUpperBound, maxHp);
    if (member.maxHp == null) {
      console.warn('[healAllyInParty] member.maxHp missing for', targetId, '— heartbeat will reconcile');
    }
    try {
      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch + return true/false.
      await update(ref(database, `parties/${partyId}/members/${targetId}`), { hp: newHp });
      return true;
    } catch (err) {
      console.error('❌ Error updating ally HP:', err);
      return false;
    }
  }, [partyId, partyMembers]);

  /**
   * Remove an effect from an ally
   */
  const removeEffectFromAlly = useCallback(async (targetId, effectId) => {
    if (!partyId || !database) {
      console.warn('Cannot remove effect: missing party ID');
      return false;
    }

    try {
      const effectRef = ref(database, `parties/${partyId}/activeEffects/${targetId}/${effectId}`);
      // [FA-002 FIX 2026-05-09]: await so rejection lands in catch.
      await remove(effectRef);

      return true;
    } catch (error) {
      console.error('❌ Error removing effect:', error);
      setSyncError(error.message);
      return false;
    }
  }, [partyId]);

  /**
   * [BATCH 13b / P1 A-9 — APR 2026]: Cleanup all activeEffects
   * authored by the current character. Called by PartyContext.leaveParty
   * before partyId is cleared so the Firebase delete actually has the
   * partyId reference.
   *
   * Pre-fix: when caster left, effects keyed under
   * parties/{partyId}/activeEffects/{targetId}/{effectId} lingered on
   * the targets. Targets had to manually clear them — easy to miss in
   * busy sessions.
   *
   * Strategy: snapshot all activeEffects groups once; iterate each
   * targetId's children; remove() any with casterId === characterId.
   */
  const cleanupCasterEffects = useCallback(async () => {
    if (!partyId || !characterId || !database) return false;
    try {
      const allEffectsRef = ref(database, `parties/${partyId}/activeEffects`);
      const snapshot = await get(allEffectsRef);
      if (!snapshot.exists()) return true;
      const allEffects = snapshot.val() || {};
      const removals = [];
      Object.entries(allEffects).forEach(([targetId, targetEffects]) => {
        if (!targetEffects || typeof targetEffects !== 'object') return;
        Object.entries(targetEffects).forEach(([effectId, effect]) => {
          if (effect && effect.casterId === characterId) {
            const effectRef = ref(
              database,
              `parties/${partyId}/activeEffects/${targetId}/${effectId}`
            );
            removals.push(remove(effectRef));
          }
        });
      });
      await Promise.all(removals);
      return true;
    } catch (error) {
      console.error('❌ Error cleaning up caster effects:', error);
      return false;
    }
  }, [partyId, characterId]);

  /**
   * [PSR-009 FIX — MAY 2026]: Cleanup per-member writes on leave.
   *
   * Pre-fix: when a member left, their fingerprints stayed in:
   *  - sharedVault/$itemId.addedByUid (orphan items "added by [stale name]")
   *  - pushedContent/$type/$id/viewedBy/$memberId (skewed read counters)
   *  - pushedContent/lootRolls/$rollId/claims/$characterId (stale claim records)
   *
   * Strategy:
   *  - Vault items: TRANSFER ownership to DM (so DM can delete; doesn't
   *    surprise-delete loot the leaver added). DM detection uses metadata.dmUid.
   *  - viewedBy / claims: REMOVE the entries (display-only data, safe to drop).
   *
   * Called from PartyContext.leaveParty + AuthContext account-deletion +
   * SettingsPanel Leave Party button before the local state clear.
   *
   * Returns true on best-effort success; failures are logged but non-fatal.
   */
  const cleanupMemberFootprints = useCallback(async () => {
    if (!partyId || !characterId || !database) return false;
    try {
      const authUid = getAuth()?.currentUser?.uid;

      // 1. Transfer vault items added by leaving member to DM ownership
      try {
        const dmUidSnap = await get(ref(database, `parties/${partyId}/metadata/dmUid`));
        const dmUid = dmUidSnap.exists() ? dmUidSnap.val() : null;
        const vaultSnap = await get(ref(database, `parties/${partyId}/sharedVault`));
        if (dmUid && authUid && vaultSnap.exists()) {
          const vault = vaultSnap.val() || {};
          const transfers = [];
          Object.entries(vault).forEach(([itemId, entry]) => {
            if (entry && entry.addedByUid === authUid && dmUid !== authUid) {
              transfers.push(
                update(
                  ref(database, `parties/${partyId}/sharedVault/${itemId}`),
                  { addedByUid: dmUid }
                )
              );
            }
          });
          await Promise.all(transfers);
        }
      } catch (vaultErr) {
        console.warn('[cleanupMemberFootprints] vault transfer failed:', vaultErr);
      }

      // 2. Remove viewedBy + claims entries keyed by the leaving characterId
      const removals = [];
      const pushedSnap = await get(ref(database, `parties/${partyId}/pushedContent`));
      if (pushedSnap.exists()) {
        const pushed = pushedSnap.val() || {};
        Object.entries(pushed).forEach(([contentType, items]) => {
          if (!items || typeof items !== 'object') return;
          Object.entries(items).forEach(([contentId, payload]) => {
            if (!payload || typeof payload !== 'object') return;
            // viewedBy/$memberId
            if (payload.viewedBy && payload.viewedBy[characterId]) {
              removals.push(
                remove(ref(database, `parties/${partyId}/pushedContent/${contentType}/${contentId}/viewedBy/${characterId}`))
              );
            }
            // claims/$characterId (lootRolls only)
            if (payload.claims && payload.claims[characterId]) {
              removals.push(
                remove(ref(database, `parties/${partyId}/pushedContent/${contentType}/${contentId}/claims/${characterId}`))
              );
            }
          });
        });
      }
      await Promise.all(removals);
      return true;
    } catch (error) {
      console.error('❌ Error cleaning member footprints:', error);
      return false;
    }
  }, [partyId, characterId]);

  // ============================================
  // LISTENERS (Existing + Enhanced)
  // ============================================

  // Listen to party metadata and determine DM status
  useEffect(() => {
    if (!partyId || !database) return;

    const metadataRef = ref(database, `parties/${partyId}/metadata`);

    // [PB-014 FIX 2026-05-09]: Capture the listener returned by onValue
    //   and pass it to off(ref, 'value', listener). Pre-fix off(ref) detached
    //   every value listener anyone had attached to that path — including
    //   listeners owned by other unrelated effects on the same ref.
    const listener = onValue(metadataRef, (snapshot) => {
      const metadata = snapshot.val();
      setPartyMetadata(metadata);

      // Check if current character is DM
      if (metadata && metadata.dmId === characterId) {
        setIsDM(true);
      } else {
        setIsDM(false);
      }
    });

    return () => off(metadataRef, 'value', listener);
  }, [partyId, characterId]);

  // Listen to activity feed
  useEffect(() => {
    if (!partyId || !database) return;

    // [FIX APR 2026 Phase 3D.7]: Use server-side limitToLast(100) so Firebase sends
    // only the most recent 100 entries instead of the entire activity feed. Addresses
    // the bandwidth concern raised during testing — previously the whole feed was
    // transferred on every load and capped only on the client.
    const activityRef = ref(database, `parties/${partyId}/activityFeed`);
    const activityQuery = query(activityRef, limitToLast(100));

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(query, 'value', listener).
    //   The query (not the raw ref) is what onValue subscribed to, so detach the
    //   query's listener specifically.
    const listener = onValue(activityQuery, (snapshot) => {
      const activities = [];
      snapshot.forEach((child) => {
        activities.push({
          id: child.key,
          ...child.val()
        });
      });

      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setPartyActivityLog(activities.slice(0, 100));
      setIsConnected(true);
      setSyncError(null);
    }, (error) => {
      console.error('❌ Error listening to activity feed:', error);
      setIsConnected(false);
      setSyncError(error.message);
    });

    return () => {
      off(activityQuery, 'value', listener);
      setIsConnected(false);
    };
  }, [partyId]);

  // Listen to party members
  useEffect(() => {
    if (!partyId || !database) return;

    const membersRef = ref(database, `parties/${partyId}/members`);

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(ref, 'value', listener).
    const listener = onValue(membersRef, (snapshot) => {
      setPartyMembers(snapshot.val() || {});
    }, (error) => {
      console.error('❌ Error listening to party members:', error);
    });

    return () => off(membersRef, 'value', listener);
  }, [partyId]);

  // [NEW] Listen to shared vault
  useEffect(() => {
    if (!partyId || !database) return;

    const vaultRef = ref(database, `parties/${partyId}/sharedVault`);

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(ref, 'value', listener).
    const listener = onValue(vaultRef, (snapshot) => {
      setSharedVaultItems(snapshot.val() || {});
    }, (error) => {
      console.error('❌ Error listening to shared vault:', error);
    });

    return () => off(vaultRef, 'value', listener);
  }, [partyId]);

  // [NEW] Listen to pushed content
  useEffect(() => {
    if (!partyId || !database) return;

    const contentRef = ref(database, `parties/${partyId}/pushedContent`);

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(ref, 'value', listener).
    const listener = onValue(contentRef, (snapshot) => {
      const content = snapshot.val() || {};
      setPushedContent({
        shops: content.shops || {},
        notes: content.notes || {},
        calendarEvents: content.calendarEvents || {},
        lootRolls: content.lootRolls || {}
      });
    }, (error) => {
      console.error('❌ Error listening to pushed content:', error);
    });

    return () => off(contentRef, 'value', listener);
  }, [partyId]);

  // [NEW] Listen to effects on current character
  useEffect(() => {
    if (!partyId || !characterId || !database) return;

    const effectsRef = ref(database, `parties/${partyId}/activeEffects/${characterId}`);

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(ref, 'value', listener).
    const listener = onValue(effectsRef, (snapshot) => {
      const effects = [];
      snapshot.forEach((child) => {
        effects.push({
          id: child.key,
          ...child.val()
        });
      });

      setActiveEffects(effects);
    }, (error) => {
      console.error('❌ Error listening to character effects:', error);
    });

    return () => off(effectsRef, 'value', listener);
  }, [partyId, characterId]);

  // [NEW] Listen to ALL party effects
  useEffect(() => {
    if (!partyId || !database) return;

    const allEffectsRef = ref(database, `parties/${partyId}/activeEffects`);

    // [PB-014 FIX 2026-05-09]: Capture listener + pass to off(ref, 'value', listener).
    const listener = onValue(allEffectsRef, (snapshot) => {
      setAllPartyEffects(snapshot.val() || {});
    }, (error) => {
      console.error('❌ Error listening to all party effects:', error);
    });

    return () => off(allEffectsRef, 'value', listener);
  }, [partyId]);

  // [BATCH 13b / P1 A-17 — APR 2026]: Sync ALL unsynced local activity
  //   entries, not just localActivityLog[0]. Pre-fix the hook only
  //   syncedlatest-entry, so when multiple actions fired between
  //   renders (React batching: a long-rest's resource resets, etc.),
  //   only the most recent one ever reached Firebase.
  //
  // Strategy: walk forward from index 0 while entries are
  //   newer-than-last-synced; sync each. localActivityLog is
  //   newest-first per the existing convention, so we collect the
  //   prefix of unsynced entries, then sync them in chronological
  //   order (oldest first) so the Firebase log retains sequence.
  useEffect(() => {
    if (!partyId || !localActivityLog.length || !database) return;

    const lastSyncedId = lastSyncedActivityRef.current;
    const unsynced = [];
    for (const entry of localActivityLog) {
      if (!entry) continue;
      if (entry.synced) continue;
      if (entry.id === lastSyncedId) break; // reached previously-synced point
      unsynced.push(entry);
    }
    if (unsynced.length === 0) return;

    // [A1 FIX]: Skip system/equipment events — they are local-only housekeeping.
    const INTERNAL_TYPES = ['system', 'equipment'];
    unsynced.reverse().filter(e => !INTERNAL_TYPES.includes(e.type)).forEach(entry => syncActivity(entry));
    // The newest entry is at the start of localActivityLog (index 0).
    lastSyncedActivityRef.current = localActivityLog[0].id;
  }, [localActivityLog, partyId, syncActivity]);

  // [SECURITY FIX APR 2026]: Register auth UID in memberUids for Firebase rule enforcement
  // [WHY]: Firebase rules can only validate auth.uid, not characterId. This maps auth → party membership.
  useEffect(() => {
    if (!partyId || !database) return;
    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (uid) {
        set(ref(database, `parties/${partyId}/memberUids/${uid}`), true);
      }
    } catch (err) {
      // Non-critical — party still works, just without UID-based rule enforcement
    }
  }, [partyId]);

  // Update character status in party (HP, AC, initiative, passive perception, class, level)
  useEffect(() => {
    // [C1 FIX / E1 FIX]: Use currentHitPoints (canonical) not hitPoints (legacy).
    //   CharacterContext.updateHP() writes currentHitPoints; some code (potions) wrote
    //   hitPoints. The canonical field is always present (default 8 in initial state).
    //   Fall back to hitPoints only for old saves that never had currentHitPoints.
    const currentHP = characterData.currentHitPoints ?? characterData.hitPoints;
    if (!partyId || !characterId || currentHP == null || !database) return;

    // Use effectCalculator for computed stats so the party card shows live values,
    // not stale fields that may never be written to playerStats directly.
    const computedAC = effectCalculator.calculateAC(characterData, [], []) ?? 10;
    const computedPerc = effectCalculator.calculatePassivePerception(characterData, [], []) ?? (
      10 + Math.floor(((characterData.abilities?.wis || 10) - 10) / 2)
    );
    const computedInit = effectCalculator.calculateInitiative(characterData, [], [])
      ?? Math.floor(((characterData.abilities?.dex || 10) - 10) / 2);

    const statusPayload = {
      hp: currentHP,
      maxHp: characterData.maxHitPoints || currentHP,
      ac: computedAC,
      passivePerception: computedPerc,
      level: characterData.totalLevel || characterData.classes?.[0]?.level || 1,
      className: characterData.classes?.[0]?.className || characterData.class || 'Adventurer',
      // [E1 FIX]: Sync characterName so party cards show member names instead of '?'
      characterName: characterData.basics?.name || characterData.name || null,
      speed: characterData.speed || 30,
      initiative: computedInit,
      status: 'online',
      savingThrows: {
        str: effectCalculator.calculateSavingThrow(characterData, [], [], 'strength')?.total ?? 0,
        dex: effectCalculator.calculateSavingThrow(characterData, [], [], 'dexterity')?.total ?? 0,
        con: effectCalculator.calculateSavingThrow(characterData, [], [], 'constitution')?.total ?? 0,
        int: effectCalculator.calculateSavingThrow(characterData, [], [], 'intelligence')?.total ?? 0,
        wis: effectCalculator.calculateSavingThrow(characterData, [], [], 'wisdom')?.total ?? 0,
        cha: effectCalculator.calculateSavingThrow(characterData, [], [], 'charisma')?.total ?? 0,
      }
    };

    updateCharacterStatus(statusPayload);

    const interval = setInterval(() => {
      updateCharacterStatus(statusPayload);
    }, 30000);

    return () => clearInterval(interval);
  // [C1 FIX]: Added currentHitPoints to deps so status re-syncs when canonical HP changes
  }, [partyId, characterId, characterData.currentHitPoints, characterData.hitPoints, characterData.maxHitPoints, characterData.armorClass, characterData.passivePerception, characterData.totalLevel, characterData.speed, characterData.abilities, characterData.proficiencies, characterData.basics, characterData.name, updateCharacterStatus]);

  return {
    // Core sync
    partyMembers,
    partyActivityLog,
    isConnected,
    syncError,
    syncActivity,
    updateCharacterStatus,

    // [NEW] Permissions
    isDM,
    claimDM,  // [FIX JAN 2026 - Issue #20]: Claim/release DM status
    canPushContent: isDM,
    canManageVault: isDM,
    partyMetadata,

    // [FIX APR 2026 Phase 3D.4/3D.7]: DM housekeeping actions
    clearActivityLog,
    kickMember,

    // [NEW] Shared Vault
    sharedVaultItems,
    addToSharedVault,
    takeFromSharedVault,
    removeFromSharedVault,

    // [NEW] DM Push Features
    pushShopToParty,
    pushNoteToParty,
    pushCalendarEventToParty,
    pushLootRollToParty,
    claimLootItem,
    markPushedContentViewed, // [B1-B3 FIX]: Acknowledge pushed notes/events/shops
    pushedContent,

    // [NEW] Spell/Effect Targeting
    applyEffectToAlly,
    healAllyInParty,  // [C2 FIX]: Update ally HP display in party panel after healing
    removeEffectFromAlly,
    activeEffects,
    allPartyEffects,
    // [BATCH 13b / P1 A-9 — APR 2026]: Caller-driven cleanup of
    //   caster-authored effects on party-leave (PartyContext.leaveParty
    //   awaits this before clearing partyId).
    cleanupCasterEffects,
    // [PSR-009 FIX — MAY 2026]: Vault-ownership transfer + viewedBy/claims
    //   cleanup on member-leave. Caller awaits before partyId is cleared.
    cleanupMemberFootprints,
  };
};

export default useEnhancedPartySync;
